import { Button, Collapse } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { motion } from 'motion/react';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { PropertyTable } from '#core/components/layout/PropertyTable.tsx';

import { DefaultStatus } from './DefaultStatus.tsx';
import classes from './ToolDisplay.module.css';
import { LoadingShimmer } from '#plugin-chat/components/shared/LoadingShimmer.tsx';
import { useToolMessage } from '#plugin-chat/hooks/useToolMessage.ts';
import { useToolPart } from '#plugin-chat/hooks/useToolPart.ts';
import { renderInstructions } from '#plugin-chat/lib/instructions/reconciler.ts';
import { parseResultEnvelope } from '#plugin-chat/lib/tool/result-envelope.ts';
import { callResultFormatter } from '#plugin-chat/lib/tool/result-formatter.ts';

export interface ToolStatusProps {
  children: Exclude<React.ReactNode, boolean | null | undefined>[];
  /** Show default status when no children are provided */
  showDefault?: boolean;
}

interface ToolResultDetailsProps {
  toolName: string | undefined;
  args: Record<string, unknown> | undefined;
  result: unknown;
}

/**
 * Render the rendered tool-result message as React (HTML) for the
 * "Show details" panel.
 *
 * The same `message.result` formatter that powers the LLM transcript
 * also runs here — but the JSX it returns is rendered through standard
 * React DOM (so users see proper tables, lists, and code styling)
 * instead of through `renderInstructions` (which produces markdown text
 * for the LLM). This keeps the UI faithful to the prose the LLM sees.
 *
 * Formatter dispatch (`{args, output}`) is delegated to
 * {@link callResultFormatter} — the same helper `useRerunTool` uses, so
 * the UI and the debug-console paths cannot drift.
 *
 * Failures: read `output.errorMessage` (already rendered at execute
 * time) verbatim.
 */
function ToolResultDetails({ toolName, args, result }: ToolResultDetailsProps) {
  const message = useToolMessage(toolName);
  const envelope = parseResultEnvelope(result);

  return (
    <PropertyTable
      data={{
        input: args,
        ...(envelope.kind === 'success' && {
          message: renderInstructions(callResultFormatter(message, args ?? {}, result)),
        }),
        output: result,
      }}
    />
  );
}

export function ToolStatus({ children, showDefault = false }: ToolStatusProps) {
  const hasChildren = children?.length > 0;

  if (!hasChildren && !showDefault) {
    return null;
  }

  return (
    <motion.div
      animate={{ opacity: 1, x: 0 }}
      className={classes.status}
      initial={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
    >
      {hasChildren ? children : <DefaultStatus />}

      <ToolDetails />
    </motion.div>
  );
}

function ToolDetails() {
  const [opened, { toggle }] = useDisclosure(false);
  const { args, result, toolName } = useToolPart();

  if (!result) {
    return null;
  }

  return (
    <>
      <div className={classes.actions}>
        <Button color="dark.3" fw="normal" h="lg" onClick={toggle} radius="xl" size="xs" variant="outline">
          Show details
        </Button>
      </div>

      <Collapse className={classes.responseObject} expanded={opened}>
        {opened && (
          <div className={classes.responseTable}>
            <Suspense fallback={<LoadingShimmer />}>
              <ErrorBoundary fallback={<PropertyTable data={{ error: true, input: args }} />}>
                <ToolResultDetails
                  args={args as Record<string, unknown> | undefined}
                  result={result}
                  toolName={toolName}
                />
              </ErrorBoundary>
            </Suspense>
          </div>
        )}
      </Collapse>
    </>
  );
}
